import { isBoolean, isPromise } from 'remeda'
import { composeCleanup } from '../helpers/cleanup'
import { bindLiveProps, type LivePropValues } from '../helpers/live-props'
import {
  readServerOpen,
  setupVisibilityCommands,
  type VisibilityHookContext
} from '../helpers/visibility'

// Private types ---------------------------------------------------------------

type UpdatableModalElement = HTMLElement & {
  readonly updateComplete: Promise<unknown>
}

type AccessibleModalElement = HTMLElement & {
  readonly dialog?: HTMLElement
  readonly drawer?: HTMLElement
}

type ModalVisibility = 'closed' | 'open'

type ModalCommandState = Readonly<{
  desired: ModalVisibility
  hideStartedAt?: number
  lastShowAt?: number
  revision: number
}>

// Public types ----------------------------------------------------------------

export type ModalElement = HTMLElement & {
  open: boolean
}

export type ModalHookContext = VisibilityHookContext

export type ModalController = Readonly<{
  cleanup: () => void
  sync: () => void
}>

// Private functions -----------------------------------------------------------

// Narrows a hook host to a modal element with an open property.
const isModalElement = (element: HTMLElement): element is ModalElement =>
  'open' in element && isBoolean(element.open)

// Narrows a modal that exposes an async update boundary.
const isUpdatableModalElement = (
  element: HTMLElement
): element is UpdatableModalElement =>
  'updateComplete' in element && isPromise(element.updateComplete)

// Reads whether the server prevents user dismissal.
const preventsDismiss = (element: HTMLElement): boolean =>
  element.dataset.preventDismiss === 'true'

// Finds Web Awesome's internal modal close button.
const modalCloseButton = (modal: HTMLElement): HTMLElement | null => {
  const closeButton = modal.shadowRoot?.querySelector('[part~="close-button"]')

  return closeButton instanceof HTMLElement ? closeButton : null
}

// Finds the internal dialog surface used for accessible naming.
const modalDialog = (modal: HTMLElement): HTMLElement | null => {
  const { dialog, drawer } = modal as AccessibleModalElement
  const element = dialog ?? drawer

  return element instanceof HTMLElement ? element : null
}

// Applies the accessible name expected by the rendered header mode.
const applyModalAccessibleName = (modal: HTMLElement): void => {
  const dialog = modalDialog(modal)

  if (!dialog) {
    return
  }

  if (modal.hasAttribute('without-header')) {
    dialog.removeAttribute('aria-labelledby')
    const label = modal.getAttribute('label')

    if (label) {
      dialog.setAttribute('aria-label', label)
    } else {
      dialog.removeAttribute('aria-label')
    }

    return
  }

  dialog.removeAttribute('aria-label')
  dialog.setAttribute('aria-labelledby', 'title')
}

// Applies accessible naming after Web Awesome finishes rendering.
const syncAccessibleName = (modal: HTMLElement): void => {
  if (!isUpdatableModalElement(modal)) {
    applyModalAccessibleName(modal)
    return
  }

  modal.updateComplete.then(() => applyModalAccessibleName(modal))
}

// Applies desired visibility to a compatible modal element.
const setOpen = (element: HTMLElement, open: boolean): void => {
  if (isModalElement(element)) {
    element.open = open
  }
}

// Creates command state from the component's current visibility.
const initialCommandState = (element: HTMLElement): ModalCommandState => {
  const desired: ModalVisibility =
    isModalElement(element) && element.open ? 'open' : 'closed'

  return {
    desired,
    lastShowAt: desired === 'open' ? 0 : undefined,
    revision: 0
  }
}

// Advances command state for one requested visibility.
const applyCommand = (
  state: ModalCommandState,
  desired: ModalVisibility
): ModalCommandState => {
  const revision = state.revision + 1

  return {
    ...state,
    desired,
    lastShowAt: desired === 'open' ? revision : state.lastShowAt,
    revision
  }
}

// Detects a show request that arrived after hiding started.
const shouldReopenAfterHide = (state: ModalCommandState): boolean =>
  state.desired === 'open' &&
  state.hideStartedAt !== undefined &&
  state.lastShowAt !== undefined &&
  state.lastShowAt > state.hideStartedAt

// Registers one event listener and returns its cleanup.
const listen = (
  target: EventTarget,
  type: string,
  listener: EventListener
): (() => void) => {
  target.addEventListener(type, listener)

  return () => target.removeEventListener(type, listener)
}

// Creates visibility and transition handling for one modal hook.
const createModalController = (
  context: ModalHookContext,
  name: 'dialog' | 'drawer'
): ModalController => {
  syncAccessibleName(context.el)

  let active = true
  let commandState = initialCommandState(context.el)

  // Mirrors prevent-dismiss state into the internal close button.
  const syncCloseButton = (): void => {
    // Applies only while this controller still owns the modal.
    const apply = (): void => {
      if (!active) {
        return
      }

      const closeButton = modalCloseButton(context.el)
      const preventDismiss = context.el.dataset.preventDismiss

      if (closeButton && preventDismiss !== undefined) {
        closeButton.hidden = preventDismiss === 'true'
      }
    }

    if (isUpdatableModalElement(context.el)) {
      context.el.updateComplete.then(apply)
    } else {
      apply()
    }
  }

  // Records and applies one requested visibility. A hide already in flight
  // needs no second request: Web Awesome holds `open` true until its hide
  // animation ends, so writing false again only restarts that animation.
  const setDesiredVisibility = (desired: ModalVisibility): void => {
    const hiding = commandState.hideStartedAt !== undefined

    commandState = applyCommand(commandState, desired)

    if (!(desired === 'closed' && hiding)) {
      setOpen(context.el, desired === 'open')
    }
  }

  // Starts hiding unless the rendered server contract prevents it.
  const handleWaHide = (event: Event): void => {
    if (event.target !== context.el) {
      return
    }

    if (preventsDismiss(context.el) && readServerOpen(context.el) === true) {
      event.preventDefault()
      return
    }

    commandState = {
      ...commandState,
      desired: 'closed',
      hideStartedAt: commandState.revision
    }
  }

  // Completes hiding or reopens after a newer show command.
  const handleWaAfterHide = (event: Event): void => {
    if (event.target !== context.el) {
      return
    }

    const reopen = shouldReopenAfterHide(commandState)
    commandState = {
      ...commandState,
      desired: reopen ? 'open' : 'closed',
      hideStartedAt: undefined
    }

    if (reopen) {
      if (!isUpdatableModalElement(context.el)) {
        setOpen(context.el, true)
        return
      }

      context.el.updateComplete.then(() => {
        if (active && commandState.desired === 'open') {
          setOpen(context.el, true)
        }
      })
    }
  }

  const cleanupCommands = setupVisibilityCommands(context, name, {
    hide: () => setDesiredVisibility('closed'),
    show: () => setDesiredVisibility('open')
  })
  const cleanupLiveProps = bindLiveProps(context.el, {
    // Reports the requested visibility rather than the element's own `open`:
    // Web Awesome keeps that true throughout the hide animation, and a patch
    // landing in that window would otherwise preserve a stale "open" and
    // reopen the modal once the hide finishes.
    read: (properties): LivePropValues =>
      properties.includes('open') && isModalElement(context.el)
        ? { open: commandState.desired === 'open' }
        : {},
    apply: (values) => {
      if (isBoolean(values.open)) {
        setDesiredVisibility(values.open ? 'open' : 'closed')
      }
    }
  })
  const cleanupListeners = composeCleanup(
    listen(context.el, 'wa-hide', handleWaHide),
    listen(context.el, 'wa-after-hide', handleWaAfterHide)
  )
  syncCloseButton()

  return {
    cleanup: () => {
      active = false
      cleanupLiveProps()
      cleanupCommands()
      cleanupListeners()
    },
    sync: () => {
      if (!active) {
        return
      }

      syncCloseButton()
    }
  }
}

// Public functions ------------------------------------------------------------

// Synchronizes the internal dialog's accessible name after rendering.
export const syncModalAccessibleName = (modal: HTMLElement): void =>
  syncAccessibleName(modal)

// Creates the modal controller used by dialog and drawer hooks.
export const setupModalCommands = (
  context: ModalHookContext,
  name: 'dialog' | 'drawer'
): ModalController => createModalController(context, name)
